import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Button, FormHelperText, TextField } from '@mui/material';
import useMounted from '../../../../hooks/useMounted';
import LoginIcon from '@mui/icons-material/Login';

import { alpha, styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { login, loginPetroHub } from '../../../../store/actions/authActions';
import { useEffect, useRef, useState } from 'react';
// import apiConfig from '../../../../services/apiConfig';

const textColor = alpha('#f4f5f7', 0.8);
const CustomTextField = styled(TextField)({
  '& input': {
    color: textColor,
  },
  '& label.Mui-focused': {
    color: textColor,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: textColor,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: textColor,
    },
    '&:hover fieldset': {
      borderColor: '#6b778c',
    },
    '&.Mui-focused fieldset': {
      borderColor: textColor,
    },
    '& input:-webkit-autofill': {
      transition: 'background-color 0s 0s, color 0s 0s',
      transitionDelay: 'calc(infinity * 1s)',
    },
    '& input:-webkit-autofill:focus': {
      transition: 'background-color 0s 0s, color 0s 0s',
      transitionDelay: 'calc(infinity * 1s)',
    },
    '& input:-webkit-autofill ~ label': {
      transform: 'translate(14px, -9px) scale(0.75)',
      backgroundColor: 'transparent',
    },
  },
});

const LoginForm = (props) => {
  const mounted = useMounted();
  const dispatch = useDispatch();
  const usernameInputRef = useRef(null);
  const [isAutofilled, setIsAutofilled] = useState(false);
  const domain = window.location.hostname;
  const isLoginWithPetroHub = domain.toLocaleLowerCase().includes('petrohub') || process.env.REACT_APP_IS_USER_LOGGED_BY_PETROHUB === 'true'; // apiConfig.IS_USER_LOGGED_BY_PETROHUB;
  console.log('Login attempt with isLoginWithPetroHub: ', isLoginWithPetroHub);
  console.log('Login--', process.env.REACT_APP_IS_USER_LOGGED_BY_PETROHUB);

  useEffect(() => {
    const el = usernameInputRef.current;
    if (!el) return;
    const handleAnimationStart = (e) => {
      if (e.animationName === 'mui-auto-fill') {
        setIsAutofilled(true);
      }
    };
    const handleAnimationEnd = (e) => {
      if (e.animationName === 'mui-auto-fill-cancel') {
        setIsAutofilled(false);
      }
    };

    el.addEventListener('animationstart', handleAnimationStart);
    el.addEventListener('animationend', handleAnimationEnd);

    // eslint-disable-next-line consistent-return
    return () => {
      el.removeEventListener('animationstart', handleAnimationStart);
      el.removeEventListener('animationend', handleAnimationEnd);
    };
  }, []);

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      if (!isAutofilled && usernameInputRef.current) {
        usernameInputRef.current.focus();
      }
    }, 150);

    return () => clearTimeout(focusTimer);
  }, [isAutofilled]);

  const onLoginHandler = async (values, { setErrors, setStatus, setSubmitting }) => {
    try {
      console.log('Login attempt with isLoginWithPetroHub: ', isLoginWithPetroHub);
      if (isLoginWithPetroHub) {
        await dispatch(loginPetroHub({ username: values.username, password: values.password }));
      } else {
        await dispatch(login({ username: values.username, password: values.password }));
      }
      if (mounted.current) {
        setStatus({ success: true });
        setSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) {
        setStatus({ success: false });
        setErrors({ submit: err.message });
        setSubmitting(false);
      }
    }
  };

  return (
    <Formik
      initialValues={{
        username: '',
        password: '',
        submit: null,
      }}
      validationSchema={Yup.object().shape({
        username: Yup.string().max(255).required('Username is required'),
        password: Yup.string().max(255).required('Password is required'),
      })}
      onSubmit={onLoginHandler}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <form noValidate onSubmit={handleSubmit} {...props} style={{ opacity: 1 }}>
          <CustomTextField
            inputRef={usernameInputRef}
            error={Boolean(touched.username && errors.username)}
            fullWidth
            helperText={touched.username && errors.username}
            label="Username"
            margin="normal"
            name="username"
            onBlur={handleBlur}
            onChange={handleChange}
            type="text"
            value={values.username}
            variant="outlined"
            autoComplete="username"
            InputLabelProps={{
              shrink: values.username.length > 0 || isAutofilled
            }}
          />

          <CustomTextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            label="Password"
            margin="normal"
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            type="password"
            value={values.password}
            variant="outlined"
            autoComplete="current-password"
            InputLabelProps={{
              shrink: values.password.length > 0 || isAutofilled
            }}
          />

          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              color="primary"
              disabled={isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              startIcon={<LoginIcon />}
              sx={{
                color: alpha('#FFFFFF', 0.8),
                background: 'linear-gradient(to right, rgba(145, 133, 190, 0.5) 30%, rgba(44, 50, 255, 0.7) 100%)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 176, 240, 0.5)',
                  color: alpha('#E1E1E1', 0.8),
                }
              }}
            >
              Log In
            </Button>
          </Box>

          {/* <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              <div>
                Use <b>demo@devias.io</b> and password <b>Password123!</b>
              </div>
            </Alert>
          </Box> */}
        </form>
      )}
    </Formik>
  );
};

export default LoginForm;
